import React, { useEffect, useState } from 'react';
import { propertiesAPI } from '../lib/api';
import { toast } from 'sonner';
import { useTranslation } from '../lib/i18n';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { X, Loader2, Save } from 'lucide-react';

/**
 * Slim modal for editing the *render-shaping* fields on an existing
 * property. Specifically excludes images. Image management lives on
 * the property detail page; this modal is for fixing the fields that
 * end up on screen / in the voiceover:
 *
 *   - title       → headline + voiceover hook
 *   - description → main voiceover text
 *   - price       → overlay on most templates
 *   - location    → overlay on most templates
 *   - highlights  → small pills in some templates
 *
 * Why a quick-edit instead of a full property-edit page? After a
 * Willhaben import the user often wants to clean up *just the title*
 * (Willhaben titles are SEO spam – "ZINSHAUS in 1140 Wien - befristet
 * für 10 Jahre vermietet, mit derzeitiger Jahresnettomieteinnahme von
 * EUR 83.400,00…"). A tiny modal lets them fix that in 5 seconds and
 * get back to picking a template. No page navigation, no scroll.
 *
 * Props
 *   property       . The Property row to edit (read-only. We copy fields)
 *   onClose()      . Called when user dismisses (X, backdrop, Escape)
 *   onSaved(prop)  . Called after successful PUT, with the refreshed Property
 */
export default function PropertyQuickEditModal({ property, onClose, onSaved }) {
  const { t } = useTranslation();

  const [form, setForm] = useState({
    title: property.title || '',
    description: property.description || '',
    price: property.price || '',
    location: property.location || '',
    highlights: (property.highlights || []).join(', '),
  });
  const [saving, setSaving] = useState(false);

  // Close on Escape. Same UX users expect from any modal.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleSave = async (e) => {
    e?.preventDefault();
    if (!form.title.trim() || form.title.trim().length < 3) {
      toast.error(t('quickEdit.titleTooShort') || 'Titel ist zu kurz.');
      return;
    }
    setSaving(true);
    try {
      const highlights = form.highlights
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);
      const res = await propertiesAPI.update(property.id, {
        title: form.title.trim(),
        description: form.description.trim(),
        price: form.price.trim(),
        location: form.location.trim(),
        highlights,
      });
      toast.success(t('quickEdit.saved') || 'Änderungen gespeichert.');
      onSaved?.(res.data);
    } catch (err) {
      toast.error(
        err.response?.data?.detail
        || t('quickEdit.saveFailed')
        || 'Speichern fehlgeschlagen.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/70 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      role="dialog"
      aria-modal="true"
    >
      <Card className="glass border-2 border-[#1f4d3a]/30 w-full sm:max-w-lg max-h-[92dvh] sm:max-h-[90dvh] overflow-y-auto overscroll-contain rounded-t-2xl sm:rounded-2xl border-0">
        <CardContent className="p-0">
          {/* Sticky header */}
          <div className="sticky top-0 bg-[#fffdf7]/95 backdrop-blur-md border-b border-[#e3dccb] px-5 py-4 flex items-center justify-between z-10">
            <h2 className="text-lg font-semibold text-[#23211a]">
              {t('quickEdit.title') || 'Immobiliendaten anpassen'}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-md hover:bg-[#efe9db] text-[#6f6a5c] hover:text-[#23211a] transition"
              aria-label={t('common.close') || 'Close'}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSave} className="px-5 py-5 space-y-4">
            <p className="text-xs text-[#8a8474] leading-relaxed">
              {t('quickEdit.intro')
                || 'Diese Felder erscheinen im Video. Du kannst sie jederzeit anpassen. Bilder verwaltest du auf der Detailseite der Immobilie.'}
            </p>

            <div>
              <label className="text-sm font-medium text-[#23211a] block mb-1.5">
                {t('quickEdit.titleLabel') || 'Titel'}
              </label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
                className="w-full bg-[#f7f3ea]/85 border border-[#e3dccb] rounded-md px-3 py-2 text-sm text-[#23211a] focus:outline-none focus:border-[#1f4d3a]"
                data-testid="quickedit-title"
                autoFocus
              />
              <p className="text-[11px] text-[#8a8474] mt-1">
                {t('quickEdit.titleHelp') || 'Headline + KI-Voiceover-Hook.'}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-[#23211a] block mb-1.5">
                  {t('quickEdit.priceLabel') || 'Preis'}
                </label>
                <input
                  type="text"
                  value={form.price}
                  onChange={(e) => setForm(f => ({ ...f, price: e.target.value }))}
                  className="w-full bg-[#f7f3ea]/85 border border-[#e3dccb] rounded-md px-3 py-2 text-sm text-[#23211a] focus:outline-none focus:border-[#1f4d3a]"
                  data-testid="quickedit-price"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-[#23211a] block mb-1.5">
                  {t('quickEdit.locationLabel') || 'Adresse'}
                </label>
                <input
                  type="text"
                  value={form.location}
                  onChange={(e) => setForm(f => ({ ...f, location: e.target.value }))}
                  className="w-full bg-[#f7f3ea]/85 border border-[#e3dccb] rounded-md px-3 py-2 text-sm text-[#23211a] focus:outline-none focus:border-[#1f4d3a]"
                  data-testid="quickedit-location"
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-[#23211a] block mb-1.5">
                {t('quickEdit.highlightsLabel') || 'Highlights (mit Komma trennen)'}
              </label>
              <input
                type="text"
                value={form.highlights}
                onChange={(e) => setForm(f => ({ ...f, highlights: e.target.value }))}
                placeholder="4 Zimmer, 130 m², Garten"
                className="w-full bg-[#f7f3ea]/85 border border-[#e3dccb] rounded-md px-3 py-2 text-sm text-[#23211a] focus:outline-none focus:border-[#1f4d3a]"
                data-testid="quickedit-highlights"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-[#23211a] block mb-1.5">
                {t('quickEdit.descriptionLabel') || 'Beschreibung'}
              </label>
              <textarea
                value={form.description}
                onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                rows={5}
                className="w-full bg-[#f7f3ea]/85 border border-[#e3dccb] rounded-md px-3 py-2 text-sm text-[#23211a] focus:outline-none focus:border-[#1f4d3a]"
                data-testid="quickedit-description"
              />
              <p className="text-[11px] text-[#8a8474] mt-1">
                {t('quickEdit.descriptionHelp')
                  || 'Wird vom KI-Voiceover als Grundlage verwendet. Kürzer + konkreter ist meistens besser.'}
              </p>
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={onClose}
                className="flex-1 text-[#4a463b] hover:bg-[#efe9db]"
              >
                {t('common.cancel') || 'Abbrechen'}
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="flex-1 bg-[#1f4d3a] hover:bg-[#163a2c] text-white"
                data-testid="quickedit-save"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                {t('quickEdit.saveBtn') || 'Speichern'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
